import { faCheck, faCircleCheck, faCircleX, faDownload, faTrash } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertTitle, Box, CircularProgress, IconButton, styled, Tooltip, TooltipProps, Typography } from "@mui/material";
import { OnlinePatientManagementContext, useSnackbar } from "@ngt/opms";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { ImageDocument, ImageDocumentGetSingleById, PostDeleteImageDocById, PostImageUploadVerify } from "../../api/dtos";
import { AppSettings } from "../../AppSettings";
import { ImageUploadUpdaterContext } from "../../contexts/ImageUploadUpdaterContext";
import DeleteDialog from "./DeleteDialog";
import UploadCell from "./UploadCell";
import VerifyDialog from "./VerifyDialog";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons";



// const RedFontAwesomeIconHover = styled(FontAwesomeIcon)(({theme}) => ({
//     color: theme.palette.mode === 'dark' ?
//         `${AppSettings.palette.redDark} !important` :
//         `${AppSettings.palette.redLight} !important`, 

//     '&:hover': {
//         color: theme.palette.mode === 'dark' ?
//         `${AppSettings.palette.redLight} !important` :
//         `${AppSettings.palette.redDark} !important`, 
//         background: 'none',
//     }
// }));

const StyledTyopography = styled(Typography)(() => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',

}));

const RedFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.redLight} !important` :
    `${AppSettings.palette.redDark} !important`, 
}));

const GreenFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.greenLight} !important` :
    `${AppSettings.palette.greenDark} !important`, 
}));

const OrangeFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
    color: theme.palette.mode === 'dark' ?
    `${AppSettings.palette.orangeLight} !important` :
    `${AppSettings.palette.orangeDark} !important`, 
}));

// const BlueFontAwesomeIconNoHover = styled(FontAwesomeIcon)(({theme}) => ({
//     color: theme.palette.mode === 'dark' ?
//     `${AppSettings.palette.blueLight} !important` :
//     `${AppSettings.palette.blueDark} !important`, 
// }));

const TableButtonsBox = styled(Box)(() => ({
    display: 'flex',
    gap: 8,
    height: '100%',
    alignItems: 'center',

}));

const StyledButton = styled(IconButton)(({theme}) => ({
    padding: theme.spacing(0),
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },

    '&:last-of-type': {
        marginRight: theme.spacing(0)
    }
}));

const StyledA = styled("a")(({theme}) => ({
    textDecoration: 'none',
    color: theme.palette.mode === 'dark' ?
        `${AppSettings.palette.primaryDark} !important` :
        `${AppSettings.palette.primaryLight} !important`, 

    '&:hover': {
        color: theme.palette.mode === 'dark' ?
        `${theme.palette.primary.light} !important` :
        `${theme.palette.primary.dark} !important`, 
        background: 'none',
    },
}));


const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))(({ theme }) => ({
    
    backgroundColor: theme.palette.mode === 'dark' ?
        `rgba(0, 0, 0, 0.87)` :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
    theme.palette.common.white :
        `rgba(0, 0, 0, 0.87)`,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    
}));

// const StyledTyopography = styled(Typography)(() => ({
//     display: 'flex',
//     alignItems: 'center',
//     width: '100%',
//     height: '100%',

// }));

const LoadingTypography = styled(Typography)(() => ({
    display: 'flex',
    alignItems: 'center'

}));

const DeIdentifyingBox = styled(Box)(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 12, 
    height: '100%'
}));


const FailedBox = styled(Box)(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 4
}));

interface IImageCell {
    canAdministerOpms: boolean;
    isSiteUSer: boolean;
    isCentralReviewer: boolean;
    displayImage?: boolean;
    displayCem?: boolean;
    initialImageDocument: ImageDocument | undefined;
    uploadId: number | undefined;
}


const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    let size = bytes;

    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size = size / 1024;
        unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
}

const ImageCell: FC<IImageCell> = ({
    canAdministerOpms,
    isSiteUSer,
    isCentralReviewer,
    displayImage,
    displayCem,
    initialImageDocument,
    uploadId
}) => {
    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);

    const [imageDocument, setImageDocument] = useState<ImageDocument | undefined>(initialImageDocument);

    // useEffect(() => {
    //     const fetchImageDocument = async () => {
    //         if (initialImageDocument?.id) {
    //             const response = await client.get(
    //                 new ImageDocumentGetSingleById({
    //                     id: initialImageDocument.id,
    //                 })
    //             );
    //             setImageDocument(response.form);
    //         }
    //     };

    //     fetchImageDocument();
    // }, [client, initialImageDocument]);

    const imageUploadUpdater = useContext(ImageUploadUpdaterContext)

    const { enqueueSnackbar } = useSnackbar();
    
    useEffect(() => {
        if (!imageDocument?.id) {
            return;
        }

        const unsubscribe = imageUploadUpdater.subscribe(imageDocument.id, async () => {
            //load new info for image upload
            const response = await client.get(
                new ImageDocumentGetSingleById({
                    id: imageDocument.id,
                })
            )
            
            setImageDocument(response.form);
        });

        return () => {
            unsubscribe();
        }
    }, [imageUploadUpdater, imageDocument?.id]);

    const onVerifyClick = useCallback(async (id: number | undefined) => {

        const tempImageDocument = {
            ...imageDocument,
            isVerified: false,
            // uploadToSharePointFailed: false,
            // isUploadedToSharePoint: false
        };

        setImageDocument(tempImageDocument as ImageDocument);

        try {

            await client.post(
                new PostImageUploadVerify({
                    documentId: id,
                })
            );

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Upload Verified Successfully
                    </AlertTitle>
                    This upload will no longer be editable.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Verified Failed
                    </AlertTitle>
                    An error occurred while attempting to verify the file.
                </>,
                { variant: 'critical' }
            );
        }
    
    }, [client, enqueueSnackbar, imageDocument, setImageDocument]);

    const [verifyAreYouSureOpen, setVerifyAreYouSureOpen] = useState(false);
    const [verifyId, setVerifyId] = useState<number | undefined>(undefined);

    const onVerifyAreYouSureClick = useCallback((id: number | undefined) => {
        setVerifyId(id);
        setVerifyAreYouSureOpen(true);
    }, [setVerifyId, setVerifyAreYouSureOpen]);

    const onVerifyAreYouSureClose = useCallback(() => {
        setVerifyId(undefined);
        setVerifyAreYouSureOpen(false);
    }, [setVerifyId, setVerifyAreYouSureOpen]);

    const onVerifyAreYouSureConfirm = useCallback(() => {
        onVerifyClick(verifyId);
        setVerifyId(undefined);
        setVerifyAreYouSureOpen(false);
    }, [setVerifyAreYouSureOpen, verifyId, setVerifyId, onVerifyClick]);



    const onDeleteClick = useCallback(async (id: number | undefined) => {

        const tempImageDocument = {
            ...imageDocument
        };

        try {

            await client.post(
                new PostDeleteImageDocById({
                    documentId: id,
                })
            );

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Upload Deleted Successfully
                    </AlertTitle>
                    This upload will no longer be available.
                </>,
                { variant: 'success' }
            );

            setImageDocument(undefined);
        }
        catch (error: any) {

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Deletion Failed
                    </AlertTitle>
                    An error occurred while attempting to delete the file.
                </>,
                { variant: 'critical' }
            );

            setImageDocument(tempImageDocument as ImageDocument);
        }
    
    }, [client, enqueueSnackbar, imageDocument, setImageDocument]);

    const [deleteAreYouSureOpen, setDeleteAreYouSureOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<number | undefined>(undefined);

    const onDeleteAreYouSureClick = useCallback((id: number | undefined) => {
        setDeleteId(id);
        setDeleteAreYouSureOpen(true);
    }, [setDeleteId, setDeleteAreYouSureOpen]);

    const onDeleteAreYouSureClose = useCallback(() => {
        setDeleteId(undefined);
        setDeleteAreYouSureOpen(false);
    }, [setDeleteId, setDeleteAreYouSureOpen]);

    const onDeleteAreYouSureConfirm = useCallback(() => {
        onDeleteClick(deleteId);
        setDeleteId(undefined);
        setDeleteAreYouSureOpen(false);
    }, [setDeleteAreYouSureOpen, deleteId, setDeleteId, onDeleteClick]);


    if (isCentralReviewer && !canAdministerOpms)
    {
        return (
            <>
                {
                    imageDocument?.isVerified === true ? (
                        <StyledTooltip title={`Download (${formatFileSize(imageDocument.file?.fileSize)})`}>
                            <StyledButton
                                size="medium"
                            >
                                <StyledA href={`/opms/file/download/${imageDocument.file?.id}`}>
                                    <StyledTyopography>
                                        Download ({formatFileSize(imageDocument.file?.fileSize)})
                                    </StyledTyopography>
                                </StyledA>
                            </StyledButton>
                        </StyledTooltip>
                    ) : (
                        <>
                        {
                            <StyledTyopography variant="body2">
                                Pending
                            </StyledTyopography>
                        }
                        </>
                    )
                }
            </>
        );
    }

    return (
        <>
            {
                imageDocument && (
                    <>
                        <VerifyDialog
                            verifyId={verifyId}
                            verifyAreYouSureOpen={verifyAreYouSureOpen}
                            onVerifyAreYouSureClose={onVerifyAreYouSureClose}
                            onVerifyAreYouSureConfirm={onVerifyAreYouSureConfirm}
                        />
                        <DeleteDialog
                            deleteId={deleteId}
                            deleteAreYouSureOpen={deleteAreYouSureOpen}
                            onDeleteAreYouSureClose={onDeleteAreYouSureClose}
                            onDeleteAreYouSureConfirm={onDeleteAreYouSureConfirm}
                        />
                        {
                            imageDocument.deIdentifiedFailed === true && (
                                
                                    <DeIdentifyingBox>
                                        <UploadCell
                                            uploadId={uploadId}
                                            displayImage={displayImage}
                                            displayCem={displayCem}
                                        />
                                        <StyledTooltip title={`De-identification failed, please try and upload again. If this doesn't work, please contact support.`}> 
                                            <FailedBox>
                                                <RedFontAwesomeIconNoHover icon={faCircleX} size="xl" fixedWidth />
                                                <LoadingTypography variant="body2">
                                                    De-identification Failed
                                                </ LoadingTypography>
                                            </FailedBox>
                                        </StyledTooltip>
                                    </ DeIdentifyingBox>
                            )
                        }
                        {
                            imageDocument.isDeIdentified !== undefined && 
                            imageDocument.isDeIdentified === true && (
                                <TableButtonsBox>
                                    {
                                        imageDocument?.isVerified === true ? (
                                            <StyledTooltip title={`Download (${formatFileSize(imageDocument.file?.fileSize)})`}>
                                                <StyledButton
                                                    size="medium"
                                                >
                                                    <StyledA href={`/opms/file/download/${imageDocument.file?.id}`}>
                                                        <FontAwesomeIcon icon={faDownload} fixedWidth />
                                                    </StyledA>
                                                </StyledButton>
                                            </StyledTooltip>
                                        ) : 
                                        (
                                            <>
                                                {
                                                    (canAdministerOpms || isSiteUSer) ? (
                                                        <StyledTooltip title={`Download (${formatFileSize(imageDocument.file?.fileSize)})`}>
                                                            <StyledButton
                                                                size="medium"
                                                            >
                                                                <StyledA href={`/opms/file/download/${imageDocument.file?.id}`}>
                                                                    <FontAwesomeIcon icon={faDownload} fixedWidth />
                                                                </StyledA>
                                                            </StyledButton>
                                                        </StyledTooltip>
                                                    ) : (
                                                        <>
                                                        </>
                                                    )
                                                }
                                            </>
                                        )
                                    }
                                    
                                    {
                                        (imageDocument?.isVerified === true ||
                                        imageDocument?.isVerified === false ) ? (
                                            <>
                                                {
                                                    imageDocument?.isVerified === true ? (
                                                        <StyledTooltip title={`Verified`}>
                                                            <GreenFontAwesomeIconNoHover icon={faCircleCheck} size="xl" fixedWidth />
                                                        </StyledTooltip>
                                                    ) : (
                                                        <StyledTooltip title={`Verifying`}>
                                                            <CircularProgress size="21px" />
                                                        </StyledTooltip>
                                                    )
                                                }
                                            </>
                                        ) : (
                                            <>
                                                {/* {
                                                    cemDocument?.isVerified === false ? (
                                                        <StyledTooltip title={`You may only verify 1 document per participant at a time. Please re-try after the CEM document has been verified.`}>
                                                            <StyledButton
                                                                size="medium"
                                                                sx={{ 
                                                                    "&:hover": {
                                                                        cursor: "default !important",
                                                                    },
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faCheck} fixedWidth />
                                                            </StyledButton>
                                                        </StyledTooltip>
                                                    ) : ( */}
                                                    {
                                                        canAdministerOpms && (
                                                            <StyledTooltip title={`Verify`}>
                                                                <StyledButton
                                                                    size="medium"
                                                                    onClick={(e) => {
                                                                        onVerifyAreYouSureClick(imageDocument?.id); 
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} fixedWidth />
                                                                </StyledButton>
                                                            </StyledTooltip>
                                                        )
                                                    }
                                                    
                                                    {
                                                        (canAdministerOpms || isSiteUSer) && (
                                                            <StyledTooltip title={`Delete`}>
                                                                <StyledButton
                                                                    size="medium"
                                                                    onClick={(e) => {
                                                                        onDeleteAreYouSureClick(imageDocument?.id); 
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} fixedWidth />
                                                                </StyledButton>
                                                            </StyledTooltip>
                                                        )
                                                    }
                                                    {
                                                        imageDocument.isCorrectInitials !== undefined &&
                                                        imageDocument.isCorrectInitials === false && (
                                                            <StyledTooltip title={`Participant initials did not match`}>
                                                                <OrangeFontAwesomeIconNoHover icon={faCircleQuestion} size="xl" fixedWidth />
                                                            </StyledTooltip>
                                                        ) 
                                                    }
                                                    
                                                    {/* )
                                                } */}
                                            </>
                                        )
                                    }
                                    {/* {
                                        imageDocument?.isUploadedToSharePoint !== undefined && (
                                            <>
                                                {
                                                    imageDocument?.isUploadedToSharePoint === true ? (
                                                        <StyledTooltip title={`Uploaded to SharePoint`}>
                                                            <BlueFontAwesomeIconNoHover icon={faCircleCheck} size="xl" fixedWidth />
                                                        </StyledTooltip>
                                                    ) : (
                                                        <StyledTooltip title={`Uploading to SharePoint`}>
                                                            <CircularProgress size="21px" />
                                                        </StyledTooltip>
                                                    )
                                                }
                                            </>
                                        )
                                    } */}
                                    {/* {
                                        imageDocument?.uploadToSharePointFailed === true && (
                                            <>
                                                <StyledTooltip title={`Upload to SharePoint failed, click here to re-upload`}>
                                                    <StyledButton
                                                        size="small"
                                                        sx={{
                                                            width: "26.25px",
                                                        }}
                                                        onClick={(e) => {
                                                            onVerifyAreYouSureClick(imageDocument?.id); 
                                                            e.stopPropagation();
                                                        }}
                                                    >
                                                        <RedFontAwesomeIconHover icon={faCircleX} size="xl" style={{ width: "21px", height: "21px"}} fixedWidth />
                                                    </StyledButton>
                                                </StyledTooltip>
                                            </>
                                        )
                                    } */}
                                </ TableButtonsBox>
                            )  
                        }
                        {
                            imageDocument.isDeIdentified !== undefined && 
                            imageDocument.isDeIdentified === false &&
                            imageDocument.isVerified !== false  && (
                                <StyledTooltip title={`This upload is being de-identified, you can't make any changes until this is done.`}>
                                    <DeIdentifyingBox>
                                        <CircularProgress size="21px"/>
                                        <LoadingTypography variant="body2">
                                            De-identifying...
                                        </ LoadingTypography>
                                        
                                    </ DeIdentifyingBox>
                                </StyledTooltip>
                            )  
                        }
                        {
                            !imageDocument.file && (
                                <UploadCell
                                    uploadId={uploadId}
                                    displayImage={displayImage}
                                    displayCem={displayCem}
                                />
                            )
                        }
                    </>
                )
            }
            {
                !imageDocument && (
                    <UploadCell
                        uploadId={uploadId}
                        displayImage={displayImage}
                        displayCem={displayCem}
                    />
                )
            }
        </>
    );
}

export default ImageCell;